import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMenuSubMenu, getSubUserRoles } from "../../../config/api";

export interface MenuItem {
  id?: string;
  name: string;
  path: string;
  inside: { name: string; path: string }[];
}

/** One page you can open: a top-level item without children, or a child of a group. */
export interface MenuEntry {
  label: string;
  /** Absolute path, e.g. "/tools/reports". */
  to: string;
  parent?: MenuItem;
  item: MenuItem;
}

// Menu + sub-user permissions, fetched once and cached (shared by the sidebar, mobile drawer and search).
export const useSideMenu = () => {
  const staffId = localStorage.getItem("hipship-user-id") ?? "";
  const menuQuery = useQuery({
    queryKey: ["side-menu"],
    queryFn: async () => ((await getMenuSubMenu())?.data?.data ?? []) as MenuItem[],
    staleTime: 10 * 60_000,
  });
  const rolesQuery = useQuery({
    queryKey: ["sub-user-roles", staffId],
    queryFn: async () => {
      const role = (await getSubUserRoles({ staff_id: staffId }))?.data?.data?.[0];
      // modules_path is a flat list of menu paths this sub-user may open, e.g. ["order", "returns"].
      return role && role.is_admin_subuser === false ? ((role.modules_path ?? []) as string[]) : null;
    },
    staleTime: 10 * 60_000,
  });

  const items = useMemo(() => {
    const valid = (menuQuery.data ?? []).filter((i) => i && i.name && i.path && Array.isArray(i.inside));
    const allowed = rolesQuery.data;
    return allowed ? valid.filter((i) => allowed.includes(i.path)) : valid;
  }, [menuQuery.data, rolesQuery.data]);

  const entries = useMemo<MenuEntry[]>(
    () =>
      items.flatMap((item) =>
        item.inside.length === 0
          ? [{ label: item.name, to: `/${item.path}`, item }]
          : item.inside.map((sub) => ({ label: sub.name, to: `/${item.path}/${sub.path}`, parent: item, item }))
      ),
    [items]
  );

  return { items, entries, loading: menuQuery.isLoading };
};

const PINNED_KEY = "hipship-pinned-menu";
const PINNED_EVENT = "hipship-pinned-menu-change";

const readPinned = (): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(PINNED_KEY) || "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
};

/** Pinned pages (absolute paths), kept in this browser and shared between the sidebar and the mobile drawer. */
export const usePinnedMenu = () => {
  const [pinned, setPinned] = useState<string[]>(readPinned);

  useEffect(() => {
    const sync = () => setPinned(readPinned());
    window.addEventListener(PINNED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PINNED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const togglePin = useCallback((to: string) => {
    const current = readPinned();
    const next = current.includes(to) ? current.filter((p) => p !== to) : [...current, to];
    try {
      localStorage.setItem(PINNED_KEY, JSON.stringify(next));
    } catch {}
    window.dispatchEvent(new Event(PINNED_EVENT));
  }, []);

  return { pinned, togglePin };
};
